'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { faviconUrl } from '@/utils/url';
import { GlobeIcon } from 'lucide-react';
import { type ComponentProps, type ReactNode } from 'react';

export type SourcesProps = {
  className?: string;
  children: ReactNode;
};

/**
 * Container component for displaying sources/citations.
 * Uses a dropdown menu pattern to show source list on click.
 */
export const Sources = ({ className, children }: SourcesProps) => {
  return (
    <DropdownMenu>
      <div className={cn('inline-flex flex-col gap-1', className)}>
        {children}
      </div>
    </DropdownMenu>
  );
};

export type SourcesTriggerProps = {
  /** Number of sources to display in the trigger */
  count: number;
  className?: string;
};

/**
 * Trigger badge to open the sources dropdown.
 * Styled as a compact badge with globe icon.
 */
export const SourcesTrigger = ({ count, className }: SourcesTriggerProps) => (
  <DropdownMenuTrigger asChild>
    <button
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground hover:bg-secondary/80 transition-colors cursor-pointer',
        className
      )}
    >
      <GlobeIcon className="size-3" />
      <span>
        {count} {count === 1 ? 'source' : 'sources'}
      </span>
    </button>
  </DropdownMenuTrigger>
);
SourcesTrigger.displayName = 'SourcesTrigger';

export type SourcesContentProps = {
  className?: string;
  children: ReactNode;
};

/**
 * Dropdown content container for the list of sources.
 */
export const SourcesContent = ({
  className,
  children,
}: SourcesContentProps) => (
  <DropdownMenuContent
    align="start"
    className={cn('max-w-xs', className)}
  >
    {children}
  </DropdownMenuContent>
);

export type SourceProps = ComponentProps<'a'> & {
  /** Title of the source (displayed as text) */
  title?: string;
  /** Optional favicon URL */
  favicon?: string;
};

/**
 * Individual source link displayed as a dropdown menu item.
 * Opens the source URL in a new tab on click.
 */
export const Source = ({
  title,
  href,
  favicon,
  className,
  children,
}: SourceProps) => {
  let parsedHostname: string | null = null;
  if (href) {
    try {
      parsedHostname = new URL(href).hostname;
    } catch {
      // Malformed URL — fall back gracefully
    }
  }

  const displayTitle = title || parsedHostname || 'Source';

  const iconUrl =
    favicon || (parsedHostname ? faviconUrl(parsedHostname) : undefined);

  return (
    <DropdownMenuItem
      className={cn('cursor-pointer', className)}
      onClick={() => {
        if (href) window.open(href, '_blank', 'noopener,noreferrer');
      }}
    >
      <div className="inline-flex items-center gap-2 text-xs truncate max-w-full">
        {iconUrl && (
          <img
            src={iconUrl}
            alt=""
            referrerPolicy="no-referrer"
            className="size-3.5 shrink-0 rounded-sm"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
        <span className="truncate">{children || displayTitle}</span>
      </div>
    </DropdownMenuItem>
  );
};
